// src/components/CortexRuntime.tsx
import React, { useState, useEffect } from "react";
import Timeline from "../components/Timeline";
import InputBar from "../components/InputBar";
import { functions, db } from "../firebase/firebase";
import {
  collection,
  query,
  orderBy,
  doc,
  setDoc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import AgentCreatorForm from "../components/AgentCreatorForm";
import SessionSidebar from "../components/SessionSidebar";
import { useAuth, useAuthRedirect } from "../context/AuthContext";
import { useUserPlan } from "../context/UserPlanContext";
import { useLocation } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import CustomAlert from "../components/CustomAlert";

interface Task {
  id: string;
  agentName: string;
  message: string;
  timestamp: number;
}

const CortexRuntime: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [memoryContext, setMemoryContext] = useState<string | null>(null);
  const [showMemoryPopup, setShowMemoryPopup] = useState(false);
  const [sessionAgentId, setSessionAgentId] = useState<string | null>(null);
  const [sessionAgentName, setSessionAgentName] = useState<string>("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { isPro, loading: planLoading } = useUserPlan();
  const [sending, setSending] = useState(false);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);
  const [alerts, setAlerts] = useState<
    { id: number; message: string; type: "success" | "error" | "warning" }[]
  >([]);
  const [alertId, setAlertId] = useState(0);

  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  useAuthRedirect();

  const handleSetMemoryContext = (context: string) => {
    setMemoryContext(context);
    setShowMemoryPopup(true);
  };

  const showAlert = (
    message: string,
    type: "success" | "error" | "warning" = "error"
  ) => {
    const newAlert = { id: alertId, message, type };
    setAlerts((prev) => [...prev, newAlert]);
    setAlertId((prev) => prev + 1);
  };

  const closeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  // 1) Watch usage counts (messageCount, sessionCount, taskCount) in real time
  useEffect(() => {
    if (!user) return;
    const usageRef = doc(db, `users/${user.uid}/usage/data`);
    const unsubscribe = onSnapshot(usageRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as any;
        setMessageCount(data.messageCount || 0);
        setSessionCount(data.sessionCount || 0);
        setTaskCount(data.taskCount || 0);
      }
    });
    return () => unsubscribe();
  }, [user]);

  // 2) Read sessionId from URL whenever it changes
  useEffect(() => {
    if (authLoading || planLoading || !user) return;
    const params = new URLSearchParams(location.search);
    const sid = params.get("sessionId");
    if (sid !== sessionId) {
      setSessionId(sid);
    }
  }, [location.search, authLoading, planLoading, user, sessionId]);

  // 3) Once we have a sessionId, listen to that session’s document to get agentId
  useEffect(() => {
    if (!sessionId || authLoading || planLoading || !user) {
      setSessionAgentId(null);
      setSessionAgentName("");
      return;
    }

    const sessionRef = doc(db, `users/${user.uid}/sessions/${sessionId}`);
    const unsubscribe = onSnapshot(
      sessionRef,
      async (snap) => {
        if (snap.exists()) {
          const data = snap.data() as any;
          const agentId = data.agentId as string;
          setSessionAgentId(agentId);

          // Fetch agent’s name
          try {
            const agentSnap = await getDoc(
              doc(db, `users/${user.uid}/agents/${agentId}`)
            );
            if (agentSnap.exists()) {
              setSessionAgentName((agentSnap.data() as any).name || "");
            } else {
              setSessionAgentName("");
            }
          } catch (err) {
            console.error("Error fetching agent name:", err);
            setSessionAgentName("");
          }
        } else {
          // Session doc was deleted or doesn’t exist
          setSessionAgentId(null);
          setSessionAgentName("");
        }
      },
      (err) => {
        console.error("Error listening to session doc:", err);
        setSessionAgentId(null);
        setSessionAgentName("");
      }
    );
    return () => unsubscribe();
  }, [sessionId, user, authLoading, planLoading]);

  // 4) Once we have sessionAgentId, listen for that session’s tasks in real time
  useEffect(() => {
    if (!sessionId || !sessionAgentId || authLoading || planLoading || !user) {
      setTasks([]);
      return;
    }

    const tasksRef = collection(
      db,
      `users/${user.uid}/sessions/${sessionId}/tasks`
    );
    const q = query(tasksRef, orderBy("timestamp", "asc"));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const loaded = snap.docs.map((d) => ({
          ...(d.data() as Omit<Task, "id">),
          id: d.id,
        }));
        setTasks(loaded);
      },
      (err) => {
        console.error("Error fetching tasks:", err);
        setTasks([]);
      }
    );
    return () => unsubscribe();
  }, [sessionId, sessionAgentId, user, authLoading, planLoading]);

  // 5) Auto‐scroll timeline when tasks change
  useEffect(() => {
    if (authLoading || planLoading) return;
    const el = document.querySelector("#timeline-end");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [tasks, authLoading, planLoading]);

  // 6) Hide memory popup after 5s
  useEffect(() => {
    if (!showMemoryPopup) return;
    const timer = setTimeout(() => {
      setShowMemoryPopup(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [showMemoryPopup]);

  // 7) Handle sending a message to the single agent for this session
  const handleSend = async (inputText: string) => {
    if (authLoading || planLoading || !user) return;
    if (!sessionId || !sessionAgentId) return;

    // Check usage
    const usageRef = doc(db, `users/${user.uid}/usage/data`);
    const usageSnap = await getDoc(usageRef);
    let mCount = 0,
      tCount = 0;
    if (usageSnap.exists()) {
      const data = usageSnap.data() as any;
      mCount = data.messageCount || 0;
      tCount = data.taskCount || 0;
    }
    if (!isPro && mCount >= 10) {
      showAlert(
        "Free tier users are limited to 10 messages. Upgrade to Pro to send more."
      );
      return;
    }
    if (!isPro && tCount >= 50) {
      showAlert(
        "Free tier users are limited to 50 tasks. Upgrade to Pro to create more."
      );
      return;
    }

    setSending(true);
    try {
      const callRun = httpsCallable(functions, "runAgentTask");
      const result = await callRun({
        userId: user.uid,
        sessionId,
        agentId: sessionAgentId,
        inputText,
      });
      const { task, memoryUsed } = result.data as {
        task: Task;
        memoryUsed: string;
      };

      // Increment usage
      await setDoc(
        usageRef,
        {
          messageCount: mCount + 1,
          taskCount: tCount + 1,
        },
        { merge: true }
      );

      handleSetMemoryContext(memoryUsed);
    } catch (err) {
      if (err instanceof FirebaseError) {
        console.error("Function call failed:", err.message, err.code);
        showAlert(`Failed to run agent task: ${err.message}`);
      } else {
        console.error("Unexpected error:", err);
        showAlert("Failed to run agent task. See console for details.");
      }
    } finally {
      setSending(false);
    }
  };

  if (authLoading || planLoading) {
    return <div className="p-6 text-offwhite">Loading...</div>;
  }
  if (!user) {
    return null;
  }

  return (
    // <div className="flex h-screen text-sm overflow-hidden">
    <div className=" flex flex-col  md:flex-row text-sm min-h-screen md:h-screen md:overflow-hidden">
      {/* Alerts */}
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          message={alert.message}
          type={alert.type}
          onClose={() => closeAlert(alert.id)}
        />
      ))}

      {/* Sidebar Toggle Button for Mobile */}
      <header className="fixed top-0 left-0 w-full bg-secondary border-b border-darkgray p-4 flex justify-between items-center z-50 md:hidden">
        <button
          aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
          aria-expanded={isSidebarOpen}
          className="p-2 bg-teal text-offwhite rounded-lg hover:bg-teal/80 transition"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? "✖" : "☰"}
        </button>
        <h2 className="text-lg font-semibold text-offwhite">Cortex Runtime</h2>
      </header>

      <div
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-secondary border-r border-darkgray p-6 transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:relative md:translate-x-0 md:flex md:h-full transition-transform duration-300 ease-in-out`}
      >
        {/* Sidebar */}
        <SessionSidebar
          userId={user.uid}
          selectedSession={sessionId}
          onSelect={setSessionId}
          isPro={isPro}
        />
      </div>

      {/* Overlay to close sidebar on mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      {/* <div className="flex-1 flex flex-col pt-16 md:pt-0 min-h-0"> */}
      <div className="flex-1 flex flex-col pt-16 md:pt-0 min-h-0">
        {/* Header: show agent name or placeholder */}
        <div className="p-4 border-b border-darkgray bg-secondary shadow-sm flex items-center justify-between">
          {sessionAgentName ? (
            <h2 className="text-xl font-semibold text-offwhite">
              Session with: {sessionAgentName}
            </h2>
          ) : (
            <h2 className="text-xl font-semibold text-offwhite">
              No session selected
            </h2>
          )}
          <button
            onClick={() => setShowAgentForm(true)}
            className="px-4 py-2 bg-teal text-offwhite rounded-lg text-sm hover:bg-teal/80 transition"
          >
            {sessionAgentName ? "Edit Agent" : "Create Agent"}
          </button>
        </div>

        {/* Timeline of messages */}
        <div className="flex-1 overflow-y-auto p-4">
          <Timeline tasks={tasks} />
          <div id="timeline-end" />
        </div>

        {/* Memory popup */}
        {memoryContext && showMemoryPopup && (
          <div className="fixed bottom-16 right-4 max-w-md p-4 bg-secondary border border-darkgray text-xs text-offwhite rounded-lg shadow-lg animate-fade-in-out">
            <h3 className="font-semibold text-sm mb-2">Context Retrieved</h3>
            <pre className="whitespace-pre-wrap max-h-40 overflow-y-auto">
              {memoryContext}
            </pre>
          </div>
        )}

        {/* Input or usage banner */}
        {!sessionAgentId ? (
          <div className="p-4 text-center text-yellow-300 bg-yellow-900 rounded">
            Please select or create a session (agent) to start chatting.
          </div>
        ) : !isPro && (messageCount >= 10 || taskCount >= 50) ? (
          <div className="p-4 text-yellow-300 bg-yellow-900 rounded text-center">
            🔒 You’ve hit the free tier limit (
            {messageCount >= 10 ? "10 messages" : "50 tasks"}).{" "}
            <a href="/billing" className="underline text-yellow-200">
              Upgrade to Pro
            </a>{" "}
            to continue chatting.
          </div>
        ) : (
          <div className="p-4 bg-secondary border-t border-darkgray">
            <InputBar onSend={handleSend} />
            {!isPro && (
              <p className="text-xs text-gray-400 mt-2">
                Free tier: {messageCount}/10 messages, {taskCount}/50 tasks,{" "}
                {sessionCount}/3 sessions used
              </p>
            )}
          </div>
        )}

        {sending && (
          <div className="text-center py-2 text-gray-400 text-xs">
            Thinking...
          </div>
        )}
      </div>

      {/* Agent Creation/Editing Modal */}
      {showAgentForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-secondary rounded-xl p-6 max-w-xl w-full mx-4 relative">
            <button
              onClick={() => setShowAgentForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition"
            >
              ✕
            </button>
            <AgentCreatorForm userId={user.uid} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CortexRuntime;
